import { useCallback, useEffect, useRef } from 'react'
import useTranslation from 'next-translate/useTranslation'

import useGoogleMapsScript, {
  GoogleMapsLibrary,
} from '../../app/hooks/useGoogleMapsScript'
import { ScriptStatus } from '../../app/hooks/useScript'
import { GeoPosition } from '../types'
import getLanguageCode from '../../app/utils/getLanguageCode'

export default function useGeocodeService() {
  const { lang } = useTranslation()
  const geocodeService = useRef<google.maps.Geocoder>()

  const scriptStatus = useGoogleMapsScript([GoogleMapsLibrary.Geocoding])

  const fetchGeocodeResults = useCallback(
    async (geoPosition: GeoPosition) => {
      if (geocodeService.current) {
        try {
          const results = await geocodeService.current.geocode({
            location: { lat: geoPosition.latitude, lng: geoPosition.longitude },
            language: getLanguageCode(lang),
          })

          return results.results?.[0]
        } catch (error) {
          return undefined
        }
      }

      return undefined
    },
    [lang],
  )

  const fetchGeocodeByPlaceId = useCallback(
    async (placeId: string) => {
      if (geocodeService.current) {
        try {
          const results = await geocodeService.current.geocode({
            placeId,
            language: getLanguageCode(lang),
          })
          return results.results?.[0]
        } catch (error) {
          return undefined
        }
      }

      return undefined
    },
    [lang],
  )

  useEffect(() => {
    if (scriptStatus === ScriptStatus.Ready) {
      geocodeService.current = new window.google.maps.Geocoder()
    }
  }, [scriptStatus])

  return {
    scriptStatus,
    fetchGeocodeResults,
    fetchGeocodeByPlaceId,
  }
}
